/* eslint-disable no-restricted-globals */

import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, LoginMessages } from "@shared/languages/interfaces";

import Logo from "@atoms/FleetLogo";
import { useAuth } from "@hooks/useAuth";
import { useToast } from "@hooks/useToast";
import api from "@services/api";

import { AuthStateResponse } from "@shared/interfaces/auth.interface";
import { StandardReturnObject } from "@utils/useful-functions";

import * as Styled from "./styles";

interface SsoCompleteResult extends AuthStateResponse {
  return_to?: string;
}

/**
 * Topcon Identity SSO callback. Reads the OAuth fragment posted by the
 * launcher, forwards the tokens to `POST /auth/sso/complete` and feeds the
 * resulting platform JWT into `useAuth.signIn`. Falls back to
 * `/?ssoFailed=true` on any error.
 */
const SsoCallback: React.FC = () => {

  const history = useHistory();
  const { signIn, signOut } = useAuth();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const handledRef = useRef(false);

  useEffect(() => {

    // Strict-mode double-invokes useEffect; the fragment token is single-use.
    if (handledRef.current) return;
    handledRef.current = true;

    // Clear any prior session so a new SSO identity fully replaces it.
    localStorage.removeItem("@Fleet:token");
    localStorage.removeItem("@Fleet:currentSurvey");
    localStorage.removeItem("@Fleet:google");
    delete api.defaults.headers.authorization;
    signOut();

    const rawHash = window.location.hash.startsWith("#")
      ? window.location.hash.substring(1)
      : window.location.hash;
    const params = new URLSearchParams(rawHash);

    const idToken = params.get("id_token");
    const accessToken = params.get("access_token") ?? undefined;
    const returnTo = params.get("return_to") ?? "/main";

    if (!idToken) {

      addToast({
        type: "error",
        title: t(LoginMessages.ssoLoginFailed),
        description: t(LoginMessages.ssoAccessNotEnabled)
      });

      history.replace("/?ssoFailed=true");

      return;
    }

    // Strip the fragment so the token doesn't linger in window.location.
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {

      try {

        const { data } = await api.post<StandardReturnObject<SsoCompleteResult>>(
          "/auth/sso/complete",
          {
            id_token: idToken,
            access_token: accessToken,
            return_to: returnTo
          }
        );

        if (data.status !== "success" || !data.result?.access_token) {

          addToast({
            type: "info",
            title: t(GlobalMessages.alert),
            description: data.message
          });

          history.replace("/?ssoFailed=true");

          return;
        }

        await signIn({ email: "", password: "" }, data.result);

        const safeReturn = data.result.return_to && data.result.return_to.startsWith("/")
          ? data.result.return_to
          : "/main";

        history.push(safeReturn);

      } catch (error: any) {

        const message = error?.response?.data?.message
          ?? error?.response?.data?.backend
          ?? t(LoginMessages.ssoLoginFailed);

        addToast({
          type: "error",
          title: t(LoginMessages.ssoLoginFailed),
          description: message
        });

        history.replace("/?ssoFailed=true");
      }
    })();

  }, [addToast, history, signIn, signOut, t]);

  return (
    <Styled.Container>
      <Styled.Card>
        <Logo />
        <p>{t(LoginMessages.login)}…</p>
      </Styled.Card>
    </Styled.Container>
  );
};

export default SsoCallback;
